import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input } from 'antd';
import type { InputRef } from 'antd';
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { AUTH_NOTICE_KEY, SIGNUP_ENABLED } from 'constants/app-info';
import {
  AUTH_FORM_PROPS,
  AuthChip,
  AuthLayout,
  AuthNote,
  AuthSwitch,
  authStyles,
  emailRules,
  normalizeEmail,
  ON_SUBMIT,
  passwordIconRender,
  VALIDATION,
} from 'layouts/auth';
import { login, LoginNotice } from 'redux/auth/actions';
import { removeAuth } from 'redux/auth/reducers';
import { paths } from 'routes/routes';
import { RootState } from '../../types/redux';
import { useAppDisPatch } from '../../utils/common';
import { LOGIN_MESSAGES, mapLoginError } from './login-errors';

interface FormValues {
  email: string;
  password: string;
}

type Notice = LoginNotice | 'expired';

interface LoginLocationState {
  notice?: LoginNotice;
  email?: string;
}

const NOTICES: Record<Exclude<Notice, 'notAdmin'>, { type: 'success' | 'info'; text: string }> = {
  loggedOut: { type: 'success', text: 'Bạn đã đăng xuất.' },
  passwordChanged: { type: 'success', text: 'Đã đổi mật khẩu. Đăng nhập bằng mật khẩu mới.' },
  expired: {
    type: 'info',
    text: 'Phiên đăng nhập đã kết thúc, có thể do tài khoản vừa đăng nhập ở thiết bị khác. Vui lòng đăng nhập lại.',
  },
};

const readInitialNotice = (state: LoginLocationState | null): Notice | null => {
  const fromState = state?.notice;
  if (fromState === 'loggedOut' || fromState === 'passwordChanged' || fromState === 'notAdmin') {
    return fromState;
  }
  // Cờ do utils/axios-utils.ts ghi trước khi trang bị tải lại về /login (mất state điều hướng):
  // 'notAdmin' = AccessGate / interceptor rời phiên vì không có ADMIN/MOD, 'expired' = phiên hết.
  try {
    const stored = sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (stored === 'notAdmin' || stored === 'expired') return stored;
  } catch {
    // sessionStorage bị chặn: không có thông báo.
  }
  return null;
};

// Chỉ tự đặt con trỏ khi màn rộng: trên điện thoại sẽ bật bàn phím che mất nửa trang.
const isWideScreen = () => {
  try {
    return window.matchMedia('(min-width: 900px)').matches;
  } catch {
    return false;
  }
};

/**
 * Cảnh báo khi tài khoản không có ADMIN/MOD (đặc tả §5). `afterLogin`: vừa đăng nhập ở đây
 * → backend đã đá tài khoản khỏi app, nên nói thật điều đó.
 */
const NotAdminAlert = ({ afterLogin }: { afterLogin: boolean }) => (
  <Alert
    type="warning"
    showIcon
    className={authStyles.formAlert}
    message="Tài khoản này chưa có quyền vào trang quản trị"
    description={
      <div className={authStyles.alertBody}>
        <p>
          Trang quản trị chỉ dành cho tài khoản Admin hoặc MOD. Nếu bạn là nhân sự HP Media, hãy nhờ
          Admin cấp quyền. <Link to={paths.requestAccess}>Xem cách được cấp quyền</Link>
        </p>
        {afterLogin && (
          <p>
            Lần đăng nhập vừa rồi đã đăng xuất tài khoản này khỏi app HP Media. Mở app và đăng nhập
            lại để tiếp tục dùng app.
          </p>
        )}
      </div>
    }
  />
);

const LoginPage = () => {
  const dispatch = useAppDisPatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as LoginLocationState | null) || null;
  const [form] = Form.useForm<FormValues>();
  const passwordRef = useRef<InputRef>(null);
  const emailValue = Form.useWatch('email', form);

  const { error, isLoading, phase } = useSelector((state: RootState) => state.auth);
  const [notice, setNotice] = useState<Notice | null>(() => readInitialNotice(locationState));
  const [initialEmail] = useState(() => normalizeEmail(locationState?.email));
  const [autoFocus] = useState(isWideScreen);

  useEffect(() => {
    // Lỗi còn sót từ lần trước (vd. quay lại /login) không được hiện lại.
    dispatch(removeAuth());
    try {
      sessionStorage.removeItem(AUTH_NOTICE_KEY);
    } catch {
      // bỏ qua
    }
  }, [dispatch]);

  const hasServerError = !!(
    error.reason ||
    error.message ||
    error.statusCode ||
    error.networkError
  );

  const clearMessages = () => {
    if (hasServerError) dispatch(removeAuth());
    if (notice) setNotice(null);
  };

  const onFinish = async (values: FormValues) => {
    if (isLoading) return;
    setNotice(null);
    const result = await dispatch(
      login({ username: normalizeEmail(values.email), password: values.password }, navigate)
    );
    if (result !== 'ok') {
      // Giữ email, xoá mật khẩu, đưa con trỏ về ô mật khẩu.
      form.setFieldValue('password', '');
      passwordRef.current?.focus();
    }
  };

  let alert: ReactNode = null;
  if (error.reason === 'NOT_ADMIN') {
    alert = <NotAdminAlert afterLogin />;
  } else if (error.reason === 'ROLE_CHECK_FAILED') {
    alert = (
      <Alert
        type="error"
        showIcon
        className={authStyles.formAlert}
        message={LOGIN_MESSAGES.roleCheckFailed}
      />
    );
  } else if (hasServerError) {
    alert = (
      <Alert
        type="error"
        showIcon
        className={authStyles.formAlert}
        message={mapLoginError(error)}
      />
    );
  } else if (notice === 'notAdmin') {
    alert = <NotAdminAlert afterLogin={false} />;
  } else if (notice) {
    alert = (
      <Alert
        type={NOTICES[notice].type}
        showIcon
        className={authStyles.formAlert}
        message={NOTICES[notice].text}
      />
    );
  }

  const submitLabel =
    phase === 'checking' ? 'Đang kiểm tra quyền…' : isLoading ? 'Đang đăng nhập…' : 'Đăng nhập';

  return (
    <AuthLayout
      title="Đăng nhập"
      subtitle="Chào mừng bạn quay lại. Nhập email và mật khẩu để vào trang quản trị."
      badge={
        <AuthChip icon={<SafetyOutlined aria-hidden="true" />}>Chỉ dành cho Admin & MOD</AuthChip>
      }
      cardFooter={
        <>
          {SIGNUP_ENABLED && (
            <AuthSwitch prompt="Chưa có tài khoản?" to={paths.register} label="Tạo tài khoản" />
          )}
          <AuthSwitch
            prompt="Chưa có quyền truy cập?"
            to={paths.requestAccess}
            label="Xem cách được cấp quyền"
          />
        </>
      }
    >
      <Form<FormValues>
        {...AUTH_FORM_PROPS}
        form={form}
        name="login-form"
        initialValues={{ email: initialEmail }}
        onFinish={onFinish}
        onValuesChange={clearMessages}
      >
        {alert}

        <Form.Item
          name="email"
          label="Email"
          rules={emailRules}
          validateFirst
          validateTrigger="onBlur"
          normalize={normalizeEmail}
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="Nhập email của bạn"
            prefix={<MailOutlined aria-hidden="true" />}
            autoFocus={autoFocus && !initialEmail}
          />
        </Form.Item>

        <div className={authStyles.fieldWithAction}>
          <Form.Item
            name="password"
            label="Mật khẩu"
            // Chỉ bắt buộc: luật cũ 8–15 ký tự chặn nhầm mật khẩu hợp lệ (6–7 ký tự đặt qua
            // app, 16–32 ký tự khi đăng ký). Như ô email, chỉ báo khi bấm Đăng nhập.
            rules={[
              { required: true, message: VALIDATION.passwordRequired, validateTrigger: ON_SUBMIT },
            ]}
          >
            <Input.Password
              ref={passwordRef}
              autoComplete="current-password"
              placeholder="Nhập mật khẩu của bạn"
              prefix={<LockOutlined aria-hidden="true" />}
              iconRender={passwordIconRender}
              autoFocus={autoFocus && !!initialEmail}
            />
          </Form.Item>
          {/* Đặt SAU Form.Item để thứ tự Tab: mật khẩu → nút mắt → Quên mật khẩu? → Đăng nhập. */}
          <Link
            to={paths.forgotPassword}
            state={{ email: normalizeEmail(emailValue) }}
            className={authStyles.fieldAction}
          >
            Quên mật khẩu?
          </Link>
        </div>

        <Button type="primary" htmlType="submit" size="large" block loading={isLoading}>
          {submitLabel}
        </Button>

        <AuthNote>
          Đăng nhập ở đây sẽ đăng xuất tài khoản này khỏi app HP Media và các trình duyệt khác. Điện
          thoại ngừng nhận thông báo cho tới khi bạn đăng nhập lại app.
        </AuthNote>
      </Form>
    </AuthLayout>
  );
};

export default LoginPage;
